import { useId, useState } from 'react'

import { useUserStore } from '@/entities/user'
import { useAuth } from '@/features/auth'
import { Button, InputField } from '@/shared/ui'
import { designTokens } from '@/shared/lib/design-tokens'
import { typographyStyle } from '@/shared/lib/catalog'
import { cn } from '@/shared/lib'

export type DashboardUserMenuProps = {
  initials?: string
  name?: string
  email?: string
  className?: string
}

const avatarStyle = {
  width: designTokens.components.account.userMenu.avatarSize,
  height: designTokens.components.account.userMenu.avatarSize,
  borderRadius: designTokens.borders.radius.pill,
  backgroundColor: designTokens.colors.neutral.grey,
  color: designTokens.colors.text.inverse,
  ...typographyStyle(designTokens.typography.button),
  fontFamily: designTokens.typography.fontFamilies.primary,
}

const overlayStyle = {
  backgroundColor: designTokens.colors.background.overlay,
}

const panelStyle = {
  width: designTokens.components.account.userMenu.panelWidth,
  backgroundColor: designTokens.colors.background.white,
  boxShadow: designTokens.shadows.accountUserMenuPanel,
}

const panelContentStyle = {
  paddingTop: designTokens.components.account.userMenu.panelPaddingTop,
  paddingLeft: designTokens.spacing.xxxl,
  paddingRight: designTokens.spacing.xxxl,
  paddingBottom: designTokens.spacing.xxxl,
  gap: designTokens.components.account.userMenu.sectionGap,
}

const userNameStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.neutral.graphite,
  ...typographyStyle(designTokens.typography.account.userName),
  letterSpacing: designTokens.typography.account.userName.letterSpacing,
}

const userEmailStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.adminMuted,
  ...typographyStyle(designTokens.typography.account.userEmail),
  letterSpacing: designTokens.typography.account.userEmail.letterSpacing,
}

const cardStyle = {
  backgroundColor: designTokens.colors.background.adminCard,
  borderRadius: designTokens.borders.radius.lg,
  border: `${designTokens.borders.widths.thin} ${designTokens.borders.styles.solid} ${designTokens.colors.border.adminPattern}`,
  boxShadow: designTokens.shadows.adminCard,
  padding: `${designTokens.components.account.userMenu.cardPaddingY} ${designTokens.components.account.userMenu.cardPaddingX}`,
  gap: designTokens.components.account.userMenu.cardGap,
}

const cardTitleStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.primary,
  ...typographyStyle(designTokens.typography.account.cardTitle),
  letterSpacing: designTokens.typography.account.cardTitle.letterSpacing,
}

const fieldLabelStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.adminLabel,
  ...typographyStyle(designTokens.typography.account.fieldLabel),
  letterSpacing: designTokens.typography.account.fieldLabel.letterSpacing,
}

const inputStyle = {
  height: designTokens.components.account.userMenu.inputHeight,
  backgroundColor: designTokens.colors.background.adminInput,
  borderRadius: designTokens.borders.radius.menu,
  border: `${designTokens.borders.widths.thin} ${designTokens.borders.styles.solid} ${designTokens.colors.border.adminPattern}`,
  padding: `${designTokens.spacing.sm} ${designTokens.spacing.lg}`,
  color: designTokens.colors.text.adminValue,
  fontFamily: designTokens.typography.fontFamilies.primary,
  ...typographyStyle(designTokens.typography.account.inputValue),
  letterSpacing: designTokens.typography.account.inputValue.letterSpacing,
}

const secondaryActionStyle = {
  height: designTokens.components.account.userMenu.actionHeight,
  borderRadius: designTokens.borders.radius.sm,
  border: `${designTokens.borders.widths.thin} ${designTokens.borders.styles.solid} ${designTokens.colors.border.adminPattern}`,
  backgroundColor: designTokens.colors.background.white,
  color: designTokens.colors.text.primary,
  fontFamily: designTokens.typography.fontFamilies.primary,
  paddingLeft: designTokens.components.account.userMenu.closePaddingX,
  paddingRight: designTokens.components.account.userMenu.closePaddingX,
  ...typographyStyle(designTokens.typography.admin.menuItem),
  letterSpacing: designTokens.typography.admin.menuItem.letterSpacing,
}

const descriptionStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.graphite,
  ...typographyStyle(designTokens.typography.body.base),
}

const openInNewTab = (url: string) => {
  window.open(url, '_blank', 'noopener,noreferrer')
}

const UserMenuCard = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="flex w-full flex-col" style={cardStyle}>
    <p style={cardTitleStyle}>{title}</p>
    {children}
  </div>
)

const UserField = ({ label, value }: { label: string; value: string }) => {
  const fieldId = useId()

  return (
    <InputField
      id={fieldId}
      label={label}
      value={value}
      readOnly
      containerClassName="w-full"
      labelStyle={fieldLabelStyle}
      inputClassName="w-full"
      style={inputStyle}
    />
  )
}

export function DashboardUserMenu({
  initials = 'K',
  name = 'Екатерина Михайленко',
  email,
  className,
}: DashboardUserMenuProps) {
  const userEmail = useUserStore((state) => state.user?.email)
  const { logout } = useAuth()
  const [open, setOpen] = useState(false)
  const panelId = useId()
  const resolvedEmail = email ?? userEmail ?? 'john.doe@acmecorp.com'

  return (
    <div className={cn('relative', className)}>
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label="Открыть меню пользователя"
        onClick={() => setOpen(true)}
        style={avatarStyle}
      >
        {initials}
      </button>
      {open ? (
        <div className="fixed inset-0 z-50">
          <button
            type="button"
            className="absolute inset-0"
            style={overlayStyle}
            aria-label="Закрыть меню пользователя"
            onClick={() => setOpen(false)}
          />
          <aside
            id={panelId}
            role="dialog"
            aria-modal="true"
            className="absolute right-0 top-0 flex h-full flex-col"
            style={panelStyle}
          >
            <div className="flex h-full flex-col" style={panelContentStyle}>
              <div className="flex w-full items-start justify-between">
                <div className="flex flex-col" style={{ gap: designTokens.spacing.xs }}>
                  <p style={userNameStyle}>{name}</p>
                  <p style={userEmailStyle}>{resolvedEmail}</p>
                </div>
                <button type="button" style={secondaryActionStyle} onClick={() => setOpen(false)}>
                  Закрыть
                </button>
              </div>
              <div
                className="flex w-full flex-col"
                style={{ gap: designTokens.components.account.userMenu.sectionGap }}
              >
                <UserMenuCard title="Линая информация">
                  <div className="flex w-full flex-col" style={{ gap: designTokens.spacing.md }}>
                    <UserField label="Имя" value="Екатерина" />
                    <UserField label="Фамилия" value="Михайленко" />
                  </div>
                </UserMenuCard>
                <UserMenuCard title="Хотите делиться опытом в подписке?">
                  <p style={descriptionStyle}>
                    Пожалуйста, заполните форму на сайте.
                    <br />
                    Мы тщательно отбираем экспертов и спикеров, и обязательно свяжемся с вами, если
                    у нас случится «мэтч».
                  </p>
                  <Button
                    type="button"
                    variant="secondarySubmitMobile"
                    className="w-full"
                    style={{ minHeight: designTokens.components.account.userMenu.actionHeight }}
                    onClick={() => openInNewTab('https://epicgrowth.io/#form-speaker')}
                  >
                    Поработать с Epic Growth
                  </Button>
                </UserMenuCard>
                <Button
                  type="button"
                  variant="secondaryAssist"
                  className="w-full"
                  style={{ minHeight: designTokens.components.account.userMenu.actionHeight }}
                  onClick={async () => {
                    setOpen(false)
                    await logout()
                  }}
                >
                  Выход
                </Button>
              </div>
            </div>
          </aside>
        </div>
      ) : null}
    </div>
  )
}

DashboardUserMenu.displayName = 'DashboardUserMenu'
